import { useMemo, JSX } from "react";

import {
  GeneralInformation,
  PlanRates,
  CriticalIllnessBenefits,
  CriticalIllnessCoverage,
  CriticalIllnessPlan,
} from "../../constants/aquafina";
import ContributionType from "../sections/contribution-type-section";
import { RangeIterator } from "../../utils";
import Rates from "../rates";
import CollapsableSection from "../sections/collapsable-section";
import { CoverageHeaderRow, CoverageRow, NotProvidedRow } from "../table/rows";
import {
  CoverageCell,
  CoverageVectorCell,
  CoverageVectorHeaderCell,
  INCONSISTENT_DATA,
} from "../table/cells";
import { ViewMode } from "../../constants/types";
import { VIEW_MODES } from "../../constants";
import PolicyGeneralInfo from "../sections/policy-general-info";
import ProposalGeneralInfo from "../sections/proposal-general-info";

type Props = {
  viewMode: ViewMode;
  coverage: CriticalIllnessCoverage;
  displayV4Fields: boolean;
};

const CriticalIllness = ({ coverage, viewMode, displayV4Fields }: Props) => {
  const state = useMemo(() => {
    const benefitDetails: CriticalIllnessBenefits[] = [];
    const generalInfo: GeneralInformation[] = [];
    const contributionTypes = new Map<string, string[]>();
    const rates: PlanRates[] = [];
    const plans: CriticalIllnessPlan[] = [];
    let inconsistentRateData = false;
    RangeIterator(coverage.plans.length, (index) => {
      const plan = coverage.plans[index];
      benefitDetails.push(plan.benefitDetails);
      generalInfo.push(plan.generalInformation);
      rates.push(plan.planRates);
      plans.push(plan);
      if (plan.inconsistentRateData === true) {
        inconsistentRateData = true;
      }
    });
    return {
      benefitDetails,
      generalInfo,
      contributionTypes,
      rates,
      plans,
      inconsistentRateData,
    };
  }, [coverage]);

  return (
    <>
      {viewMode === VIEW_MODES.POLICY ? (
        <>
          <PolicyGeneralInfo data={state.generalInfo} />
          <ContributionType plans={state.plans} />
        </>
      ) : (
        <ProposalGeneralInfo data={state.generalInfo} />
      )}
      <CriticalIllnessBenefitDetails
        benefits={state.benefitDetails}
        displayV4Fields={displayV4Fields}
      />
      <CriticalIllnessAdditionalPlanFeatures
        benefits={state.benefitDetails}
        displayV4Fields={displayV4Fields}
        viewMode={viewMode}
      />
      <Rates
        rates={state.rates}
        inconsistentRateData={state.inconsistentRateData}
        totalPlans={
          state.rates.length === 0 ? state.plans.length : state.rates.length
        }
      />
    </>
  );
};

type CriticalIllnessBenefitProps = {
  benefits: CriticalIllnessBenefits[];
  displayV4Fields: boolean;
};

type CriticalIllnessAdditionalPlanFeaturesProps =
  CriticalIllnessBenefitProps & {
    viewMode: ViewMode;
  };

const CriticalIllnessBenefitDetails = ({
  benefits,
  displayV4Fields,
}: CriticalIllnessBenefitProps): JSX.Element => (
  <CollapsableSection numberOfColumns={benefits.length} title="Benefit Details">
    <CriticalIllnessBenefitDetailsData
      benefits={benefits}
      displayV4Fields={displayV4Fields}
    />
  </CollapsableSection>
);

const CriticalIllnessBenefitDetailsData = ({
  benefits,
  displayV4Fields,
}: CriticalIllnessBenefitProps): JSX.Element => {
  if (benefits.every((b) => b === null)) {
    return <NotProvidedRow colSpan={benefits.length} />;
  }

  const formatOccurrenceOfIllness = (
    occurrence: string | null,
    waitingPeriod: string | null,
  ): string | null => {
    if (
      occurrence === "Included" &&
      waitingPeriod != null &&
      waitingPeriod !== INCONSISTENT_DATA
    ) {
      return `${occurrence} - ${waitingPeriod}`;
    }
    return occurrence;
  };

  return (
    <>
      <CoverageHeaderRow isWhite>
        {RangeIterator(benefits.length, (index) => (
          <CoverageVectorHeaderCell
            label={["Employee", "Spouse", "Child"]}
            key={index}
          />
        ))}
      </CoverageHeaderRow>
      <CoverageRow label="Benefit Description">
        {RangeIterator(benefits.length, (index) => (
          <CoverageVectorCell
            value={[
              benefits[index].employee?.benefitAmount,
              benefits[index].spouse?.benefitAmount,
              benefits[index].child?.benefitAmount,
            ]}
            key={index}
          />
        ))}
      </CoverageRow>
      <CoverageRow label="Benefit Maximum" isWhite>
        {RangeIterator(benefits.length, (index) => (
          <CoverageVectorCell
            value={[
              benefits[index].employee?.benefitMaximum,
              benefits[index].spouse?.benefitMaximum,
              "-",
            ]}
            key={index}
          />
        ))}
      </CoverageRow>
      <CoverageRow label="Guarantee Issue">
        {RangeIterator(benefits.length, (index) => (
          <CoverageVectorCell
            value={[
              benefits[index].employee?.guaranteeIssue,
              benefits[index].spouse?.guaranteeIssue,
              "-",
            ]}
            key={index}
          />
        ))}
      </CoverageRow>
      {displayV4Fields ? (
        <>
          <CoverageRow label="Invasive Cancer" isWhite>
            {RangeIterator(benefits.length, (index) => (
              <CoverageCell
                value={benefits[index].invasiveCancer}
                key={index}
              />
            ))}
          </CoverageRow>
          <CoverageRow label="Non-Invasive Cancer">
            {RangeIterator(benefits.length, (index) => (
              <CoverageCell
                value={benefits[index].nonInvasiveCancer}
                key={index}
              />
            ))}
          </CoverageRow>
          <CoverageRow label="Category 1 - Vascular Heart Attack" isWhite>
            {RangeIterator(benefits.length, (index) => (
              <CoverageCell
                value={benefits[index].category1VascularHeartAttack}
                key={index}
              />
            ))}
          </CoverageRow>
          <CoverageRow label="Category 1 - Vascular Stroke">
            {RangeIterator(benefits.length, (index) => (
              <CoverageCell
                value={benefits[index].category1VascularStroke}
                key={index}
              />
            ))}
          </CoverageRow>
        </>
      ) : null}
      <CoverageRow label="Kidney Failure" isWhite={!displayV4Fields}>
        {RangeIterator(benefits.length, (index) => (
          <CoverageCell value={benefits[index].renalFailure} key={index} />
        ))}
      </CoverageRow>
      <CoverageRow label="Major Organ Transplant" isWhite={displayV4Fields}>
        {RangeIterator(benefits.length, (index) => (
          <CoverageCell value={null} key={index} /> // TODO: replace with major organ transplant data
        ))}
      </CoverageRow>
      <CoverageRow label="Maximum Payout" isWhite={!displayV4Fields}>
        {RangeIterator(benefits.length, (index) => (
          <CoverageCell value={benefits[index].maximumPayout} key={index} />
        ))}
      </CoverageRow>
      <CoverageRow
        label="Occurrence of Different Illness"
        isWhite={displayV4Fields}
      >
        {RangeIterator(benefits.length, (index) => (
          <CoverageCell
            value={formatOccurrenceOfIllness(
              benefits[index].occurrenceOfDiffIllnessCoverage,
              benefits[index].occurrenceOfDiffIllnessWaitingPeriod,
            )}
            key={index}
          />
        ))}
      </CoverageRow>
      <CoverageRow
        label="Additional Occurrence of Same Illness"
        isWhite={!displayV4Fields}
      >
        {RangeIterator(benefits.length, (index) => (
          <CoverageCell
            value={formatOccurrenceOfIllness(
              benefits[index].additionalOccurrenceOfSameIllnessCoverage,
              benefits[index].additionalOccurrenceOfSameIllnessWaitingPeriod,
            )}
            key={index}
          />
        ))}
      </CoverageRow>
    </>
  );
};

const CriticalIllnessAdditionalPlanFeatures = ({
  benefits,
  displayV4Fields,
  viewMode,
}: CriticalIllnessAdditionalPlanFeaturesProps): JSX.Element => (
  <CollapsableSection
    numberOfColumns={benefits.length}
    title="Additional Plan Features"
  >
    <CriticalIllnessAdditionalPlanFeaturesData
      benefits={benefits}
      displayV4Fields={displayV4Fields}
      viewMode={viewMode}
    />
  </CollapsableSection>
);

const CriticalIllnessAdditionalPlanFeaturesData = ({
  benefits,
  displayV4Fields,
  viewMode,
}: CriticalIllnessAdditionalPlanFeaturesProps): JSX.Element => {
  if (benefits.every((b) => b === null)) {
    return <NotProvidedRow colSpan={benefits.length} />;
  }
  return (
    <>
      <CoverageRow label="Age Reduction Schedule" isWhite>
        {RangeIterator(benefits.length, (index) => (
          <CoverageCell value={benefits[index].ageReduction} key={index} />
        ))}
      </CoverageRow>
      <CoverageRow label="Pre-Existing Condition">
        {RangeIterator(benefits.length, (index) => (
          <CoverageCell
            value={benefits[index].preExistingCondition}
            key={index}
          />
        ))}
      </CoverageRow>
      <CoverageRow label="Portability" isWhite>
        {RangeIterator(benefits.length, (index) => (
          <CoverageCell value={benefits[index].portability} key={index} />
        ))}
      </CoverageRow>
      <CoverageRow label="Wellness Benefit">
        {RangeIterator(benefits.length, (index) => (
          <CoverageCell value={benefits[index].wellness} key={index} />
        ))}
      </CoverageRow>
      {displayV4Fields && viewMode !== VIEW_MODES.POLICY ? (
        <CoverageRow label="Annual Open Enrollment" isWhite>
          {RangeIterator(benefits.length, (index) => (
            <CoverageCell
              value={benefits[index].annualOpenEnrollment}
              key={index}
            />
          ))}
        </CoverageRow>
      ) : null}
      <CoverageRow label="Age Basis" isWhite={!displayV4Fields}>
        {RangeIterator(benefits.length, (index) => (
          <CoverageCell value={benefits[index].ageBasis} key={index} />
        ))}
      </CoverageRow>
    </>
  );
};

export default CriticalIllness;
